import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { OfflineMapState } from '#nora-ui/map/types/persistence.types';

/**
 * Pinia store for persisting map state to the native layer.
 * This store acts as a bridge between the OOP state managers and the
 * existing Pinia persistence infrastructure (persistedStorePlugin).
 *
 * State is stored per-map using the map ID as the key.
 *
 * `persist: true` because this is the one store holding state that exists nowhere else: the
 * viewport, plus draw layers created offline.
 */
export const useMapPersistenceStore = defineStore('mapPersistenceStore', () => {
  /**
   * Persisted state for each map, keyed by map ID.
   */
  const persistedMaps = ref<Record<string, OfflineMapState>>({});

  /**
   * Set the persisted state for a specific map.
   * @param mapId - The unique identifier of the map
   * @param state - The serialized offline state to persist
   */
  function setMapState(mapId: string, state: OfflineMapState): void {
    persistedMaps.value[mapId] = state;
  }

  /**
   * Get the persisted state for a specific map.
   * @param mapId - The unique identifier of the map
   * @returns The persisted state, or undefined if not found
   */
  function getMapState(mapId: string): OfflineMapState | undefined {
    return persistedMaps.value[mapId];
  }

  /**
   * Check if persisted state exists for a specific map.
   * @param mapId - The unique identifier of the map
   * @returns True if state exists for the map
   */
  function hasMapState(mapId: string): boolean {
    return mapId in persistedMaps.value;
  }

  /**
   * Remove the persisted state for a specific map.
   * Should be called when a map is destroyed.
   * @param mapId - The unique identifier of the map
   */
  function removeMapState(mapId: string): void {
    delete persistedMaps.value[mapId];
  }

  /**
   * Get all map IDs that have persisted state.
   * @returns Array of map IDs
   */
  function getPersistedMapIds(): string[] {
    return Object.keys(persistedMaps.value);
  }

  /**
   * Clear all persisted map state.
   * Use with caution - this removes all offline data.
   */
  function clearAll(): void {
    persistedMaps.value = {};
  }

  return {
    persistedMaps,
    setMapState,
    getMapState,
    hasMapState,
    removeMapState,
    getPersistedMapIds,
    clearAll,
  };
}, { persist: true });
